import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { TranslateService } from "@ngx-translate/core";
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '../../internal';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const POINT_OF_INTEREST_ENTITY_TOKEN = new StateToken<PointOfInterestEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.POINT_OF_INTEREST_ENTITY);

export interface PointOfInterestEntityStateModel extends FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.PointOfInterestEntityModels.PointOfInterest> {
  poiId: string | null;
}

@State<PointOfInterestEntityStateModel>({
  name: POINT_OF_INTEREST_ENTITY_TOKEN,
  defaults: {
    poiId: null,
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class PointOfInterestEntityState extends HistoryEntityBaseState<FlatModels.PointOfInterestEntityModels.PointOfInterest, PointOfInterestEntityStateModel> {
  //<editor-fold desc="Selectors">
	
  @Selector([PointOfInterestEntityState])
  static poiId$(state: PointOfInterestEntityStateModel): string | null {
    return state.poiId;
  }
	
  @Selector([PointOfInterestEntityState])
  static override typedEntities$(state: PointOfInterestEntityStateModel): { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest } {
    return HistoryEntityBaseState.typedEntities$(state);
  }
	
  @Selector([PointOfInterestEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest }): FlatModels.PointOfInterestEntityModels.PointOfInterest[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }
	
  @Selector([PointOfInterestEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }
	
  @Selector([PointOfInterestEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }
	
  @Selector([
    PointOfInterestEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedSlidePage$,
  ])
  static currentSelectedTypedSlidePois$(typedEntities: { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest },
                                        currentSelectedSlidePage: FlatModels.PageEntityModels.Slide | null) {
    if (currentSelectedSlidePage && currentSelectedSlidePage.pois) {
      return currentSelectedSlidePage.pois.reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr[entity.id] = entity;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest });
    }
    return {};
  }
  
  @Selector([
    PointOfInterestEntityState.typedEntities$,
    FlatStates.ImageEntityState.currentSelectedSlideImage$,
  ])
  static currentSelectedTypedImagePois$(typedEntities: { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest },
                                        currentSelectedSlideImage: FlatModels.ImageEntityModels.Image | null) {
    if (currentSelectedSlideImage && currentSelectedSlideImage.pois) {
      return currentSelectedSlideImage.pois.reduce((arr, id) => {
        const entity = typedEntities[id];
        if (entity) {
          arr[entity.id] = entity;
        }
        return arr;
      }, {} as { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest });
    }
    return {};
  }
	
  @Selector([
    PointOfInterestEntityState.currentSelectedTypedSlidePois$,
  ])
  static currentSelectedSlidePois$(currentSelectedTypedSlidePois: { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest }) {
    return Object.values(currentSelectedTypedSlidePois);
  }
	
  @Selector([
    PointOfInterestEntityState.currentSelectedTypedImagePois$,
  ])
  static currentSelectedImagePois$(currentSelectedTypedImagePois: { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest }) {
    return Object.values(currentSelectedTypedImagePois);
  }
  
  @Selector([PointOfInterestEntityState.currentSelectedSlidePois$])
  static currentSelectedSlidePoisArray$(currentSelectedSlidePois: FlatModels.PointOfInterestEntityModels.PointOfInterest[]) {
    return currentSelectedSlidePois.reduce((arr, poi) => {
      arr.push({
        value: poi.id,
        name: poi.name
      });
      return arr;
    }, [] as {value: string, name: string}[]);
  }
  
  @Selector([PointOfInterestEntityState.currentSelectedSlidePois$])
  static isAnnotationInCurrentSelectedSlidePois$(currentSelectedSlidePois: FlatModels.PointOfInterestEntityModels.PointOfInterest[]) {
    return (annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) => {
      for (const poi of currentSelectedSlidePois) {
        for (const annotationToPoi of poi.annotationToPointOfInterest) {
          if (annotation.id === annotationToPoi.annotation)
            return true;
        }
      }
      return false;
    }
  }
  
  @Selector([PointOfInterestEntityState.currentSelectedSlidePois$])
  static imagePoiOptionArray$(currentSelectedSlidePois: FlatModels.PointOfInterestEntityModels.PointOfInterest[]) {
    return (image: FlatModels.ImageEntityModels.Image, translate: TranslateService) => {
      let pois = currentSelectedSlidePois.reduce((arr, poi) => {
        if (image && !image.pois?.includes(poi.id)) {
          arr.push({
            value: poi.id,
            name: poi.name
          });
        }
        return arr;
      }, [] as {value: string, name: string}[]);
      if (pois.length > 0) {
        pois = [{value: 'all', name: translate.instant('global.poi_all')}, ...pois];
      }
      return pois;
    };
  }
  
  @Selector([
    PointOfInterestEntityState.currentSelectedTypedSlidePois$,
    PointOfInterestEntityState.poiId$
  ])
  static currentSelectedSlidePoi$(currentSelectedTypedSlidePois: { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest },
                                  poiId: string | null) {
    return poiId && currentSelectedTypedSlidePois[poiId] || null;
  }
  
  @Selector([
    PointOfInterestEntityState.currentSelectedTypedImagePois$,
    PointOfInterestEntityState.poiId$
  ])
  static currentSelectedImagePoi$(currentSelectedTypedImagePois: { [id: string]: FlatModels.PointOfInterestEntityModels.PointOfInterest },
                                  poiId: string | null) {
    return poiId && currentSelectedTypedImagePois[poiId] || null;
  }
  
  @Selector([PointOfInterestEntityState.currentSelectedSlidePoi$])
  static currentSelectedSlidePoiAnnotationOptions$(currentSelectedSlidePoi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    return (annotationOptionArray: { value: string, name: string}[],
            poi?: FlatModels.PointOfInterestEntityModels.PointOfInterest) => {
      if (!poi) {
        poi = currentSelectedSlidePoi;
      }
      return annotationOptionArray.reduce((arr, annotationOption) => {
        if (poi && !((poi.annotationToPointOfInterest || []).map(atpoi => atpoi.annotation) || []).includes(annotationOption.value)) {
          arr.push({
            value: annotationOption.value,
            name: annotationOption.name
          });
        }
        return arr;
      }, [] as { value: string, name: string }[]);
    };
  }
  
  @Selector([PointOfInterestEntityState.currentSelectedSlidePois$])
  static poisByAnnotation$(pois: FlatModels.PointOfInterestEntityModels.PointOfInterest[]) {
    return (id: string) => {
      return pois.reduce((arr, poi) => {
        for (const annotationToPointOfInterest of poi.annotationToPointOfInterest) {
          if (annotationToPointOfInterest.annotation === id) {
            arr.push(poi);
            break;
          }
        }
        return arr;
      }, [] as FlatModels.PointOfInterestEntityModels.PointOfInterest[]);
    }
  }
  
  @Selector([PointOfInterestEntityState.currentSelectedSlidePois$])
  static poiIdsByAnnotation$(pois: FlatModels.PointOfInterestEntityModels.PointOfInterest[]) {
    return (id: string) => {
      return pois.reduce((arr, poi) => {
        for (const annotationToPointOfInterest of poi.annotationToPointOfInterest) {
          if (annotationToPointOfInterest.annotation === id) {
            arr.push(poi.id);
            break;
          }
        }
        return arr;
      }, [] as string[]);
    }
  }
  
  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">
	
  @Action(FlatActions.PointOfInterestEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<PointOfInterestEntityStateModel>,
                            action: FlatActions.PointOfInterestEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }
	
  @Action(FlatActions.PointOfInterestEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<PointOfInterestEntityStateModel>,
                              action: FlatActions.PointOfInterestEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }
	
  @Action(FlatActions.PointOfInterestEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<PointOfInterestEntityStateModel>,
                               action: FlatActions.PointOfInterestEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }
	
  @Action(FlatActions.PointOfInterestEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<PointOfInterestEntityStateModel>,
                                 action: FlatActions.PointOfInterestEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">
	
  @Action(FlatActions.PointOfInterestEntityActions.AddEntity)
  @Undoable(
    FlatActions.PointOfInterestEntityActions.AddEntity,
    POINT_OF_INTEREST_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<PointOfInterestEntityStateModel>,
                     action: FlatActions.PointOfInterestEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }
	
  @Action(FlatActions.PointOfInterestEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.PointOfInterestEntityActions.UpdateEntity,
    POINT_OF_INTEREST_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<PointOfInterestEntityStateModel>,
                        action: FlatActions.PointOfInterestEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }
	
  @Action(FlatActions.PointOfInterestEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.PointOfInterestEntityActions.RemoveEntity,
    POINT_OF_INTEREST_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<PointOfInterestEntityStateModel>,
                        action: FlatActions.PointOfInterestEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">
	
  @Action(FlatActions.PointOfInterestEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<PointOfInterestEntityStateModel>,
                        action: FlatActions.PointOfInterestEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }
	
  @Action(FlatActions.PointOfInterestEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<PointOfInterestEntityStateModel>,
                        action: FlatActions.PointOfInterestEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }
	
  @Action(FlatActions.PointOfInterestEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<PointOfInterestEntityStateModel>,
                            action: FlatActions.PointOfInterestEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>
	
  @Action(FlatActions.PointOfInterestEntityActions.SetPoiId)
  setPoiId({setState}: StateContext<PointOfInterestEntityStateModel>,
           {poiId}: FlatActions.PointOfInterestEntityActions.SetPoiId) {
    setState(
      patch({
        poiId
      })
    );
  }
  
  @Action(FlatActions.PointOfInterestEntityActions.RearrangePois)
  rearrangePois({getState, dispatch}: StateContext<PointOfInterestEntityStateModel>,
                {pois}: FlatActions.PointOfInterestEntityActions.RearrangePois) {
    // Get Entities
    const entities = structuredClone((getState()).entities);
    
    // Iterate Through Pois
    for (let i = 0; i < pois.length; i++) {
      // Get Poi
      const poi = pois[i];
      
      // Get Entity
      let entity;
      if (typeof poi === 'string') {
        entity = entities[poi];
      } else {
        entity = entities[poi.id];
      }
      
      // Update Entity
      entity.order = i;
      
      // Dispatch Update
      dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(entity));
    }
  }
  
  @Action(FlatActions.PointOfInterestEntityActions.RearrangeImagePois)
  rearrangeImagePois({getState, dispatch}: StateContext<PointOfInterestEntityStateModel>,
                     {image, pois}: FlatActions.PointOfInterestEntityActions.RearrangeImagePois) {
    // Get Entities
    const entities = structuredClone((getState()).entities);
    
    // Iterate Through Pois
    const imagePoiIds: string[] = [];
    for (let i = 0; i < pois.length; i++) {
      // Get Poi
      const poi = pois[i];
      
      // Get Entity
      let entity;
      if (typeof poi === 'string') {
        entity = entities[poi];
      } else {
        entity = entities[poi.id];
      }
      
      // Update Entity
      entity.order = i;
      
      // Update Image Poi Ids
      imagePoiIds.push(entity.id);
      
      // Dispatch Update
      dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(entity));
    }
    
    // Update Image Pois
    image.pois = imagePoiIds;
    
    // Dispatch Update
    dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image));
  }
  
  @Action(FlatActions.PointOfInterestEntityActions.RearrangePoiAnnotations)
  rearrangePoiAnnotations({dispatch}: StateContext<PointOfInterestEntityStateModel>,
                          {poi, annotationToPointOfInterests}: FlatActions.PointOfInterestEntityActions.RearrangePoiAnnotations) {
    // Iterate
    let i = 0;
    for (const tmpAnnotationToPointOfInterest of annotationToPointOfInterests) {
      // Get Annotation To Point Of Interest
      const annotationToPointOfInterest = poi.annotationToPointOfInterest.find(atpoi => atpoi.id === tmpAnnotationToPointOfInterest.id);
      if (!annotationToPointOfInterest) {
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.no_entity_found',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatStates.PointOfInterestEntityState.rearrangePoiAnnotations"
        );
      }
      // Update Entity
      annotationToPointOfInterest.order = i++;
    }
    // Sort annotation array on poi by order 
    poi.annotationToPointOfInterest.sort((a, b) => a.order - b.order);
    // Dispatch Update
    dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi));
  }

  //</editor-fold>
}
